import { request } from '../base';
import type { ApiResponse, PaginatedResponse, PaginationParams } from '../base';

// Lab API
export async function getLabDashboard() {
  return request<any>('/lab/dashboard');
}

export async function getLabNotebooks(params?: { status?: string; type?: string; projectId?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.append('status', params.status);
  if (params?.type) query.append('type', params.type);
  if (params?.projectId) query.append('projectId', params.projectId);
  if (params?.search) query.append('search', params.search);
  if (params?.limit) query.append('limit', params.limit.toString());
  return request<{ items: any[]; total: number }>(`/lab/notebooks?${query.toString()}`);
}

export async function getLabNotebook(id: string) {
  return request<any>(`/lab/notebooks/${id}`);
}

export async function createLabNotebook(data: { title: string; description?: string; notebookType?: string; projectId?: string; isConfidential?: boolean; tags?: string[] }) {
  return request<any>('/lab/notebooks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateLabNotebook(id: string, data: Partial<{ title: string; description: string; notebookType: string; status: string; isConfidential: boolean; tags: string[] }>) {
  return request<any>(`/lab/notebooks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteLabNotebook(id: string) {
  return request<{ success: boolean }>(`/lab/notebooks/${id}`, { method: 'DELETE' });
}

// Lab Notebook Entries
export async function getNotebookEntries(notebookId: string, params?: { limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.limit) query.append('limit', params.limit.toString());
  if (params?.offset) query.append('offset', params.offset.toString());
  return request<{ items: any[] }>(`/lab/notebooks/${notebookId}/entries?${query.toString()}`);
}

export async function createNotebookEntry(notebookId: string, data: { title: string; content?: string; entryType?: string; observations?: string; conclusions?: string }) {
  return request<any>(`/lab/notebooks/${notebookId}/entries`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function signNotebookEntry(entryId: string) {
  return request<any>(`/lab/entries/${entryId}/sign`, { method: 'POST' });
}

export async function witnessNotebookEntry(entryId: string) {
  return request<any>(`/lab/entries/${entryId}/witness`, { method: 'POST' });
}

// Experiments
export async function getExperiments(params?: { status?: string; type?: string; projectId?: string; notebookId?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.append('status', params.status);
  if (params?.type) query.append('type', params.type);
  if (params?.projectId) query.append('projectId', params.projectId);
  if (params?.notebookId) query.append('notebookId', params.notebookId);
  if (params?.search) query.append('search', params.search);
  if (params?.limit) query.append('limit', params.limit.toString());
  return request<{ items: any[]; total: number }>(`/lab/experiments?${query.toString()}`);
}

export async function getExperiment(id: string) {
  return request<any>(`/lab/experiments/${id}`);
}

export async function createExperiment(data: { title: string; objective?: string; hypothesis?: string; experimentType?: string; priority?: string; notebookId?: string; projectId?: string }) {
  return request<any>('/lab/experiments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateExperiment(id: string, data: Partial<{ title: string; objective: string; hypothesis: string; status: string; priority: string; results: string; conclusions: string }>) {
  return request<any>(`/lab/experiments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteExperiment(id: string) {
  return request<{ success: boolean }>(`/lab/experiments/${id}`, { method: 'DELETE' });
}

// Bench Trials
export async function getBenchTrials(params?: { status?: string; experimentId?: string; formulaId?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.append('status', params.status);
  if (params?.experimentId) query.append('experimentId', params.experimentId);
  if (params?.formulaId) query.append('formulaId', params.formulaId);
  if (params?.search) query.append('search', params.search);
  if (params?.limit) query.append('limit', params.limit.toString());
  return request<{ items: any[]; total: number }>(`/lab/trials?${query.toString()}`);
}

export async function getBenchTrial(id: string) {
  return request<any>(`/lab/trials/${id}`);
}

export async function createBenchTrial(data: { title: string; objective?: string; experimentId?: string; formulaId?: string; batchSize?: string; batchUnit?: string }) {
  return request<any>('/lab/trials', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBenchTrial(id: string, data: Partial<{ title: string; objective: string; status: string; passFail: string; observations: string; results: any }>) {
  return request<any>(`/lab/trials/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function reviewBenchTrial(id: string) {
  return request<any>(`/lab/trials/${id}/review`, { method: 'POST' });
}

export async function deleteBenchTrial(id: string) {
  return request<{ success: boolean }>(`/lab/trials/${id}`, { method: 'DELETE' });
}

// Panel Evaluations
export async function getPanelEvaluations(params?: { status?: string; type?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.append('status', params.status);
  if (params?.type) query.append('type', params.type);
  if (params?.search) query.append('search', params.search);
  if (params?.limit) query.append('limit', params.limit.toString());
  return request<{ items: any[]; total: number }>(`/lab/panels?${query.toString()}`);
}

export async function getPanelEvaluation(id: string) {
  return request<any>(`/lab/panels/${id}`);
}

export async function createPanelEvaluation(data: { title: string; description?: string; evaluationType?: string; scheduledDate?: string; panelSize?: number; methodology?: string; blindCoded?: boolean }) {
  return request<any>('/lab/panels', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePanelEvaluation(id: string, data: Partial<{ title: string; description: string; status: string; evaluationType: string; panelSize: number }>) {
  return request<any>(`/lab/panels/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deletePanelEvaluation(id: string) {
  return request<{ success: boolean }>(`/lab/panels/${id}`, { method: 'DELETE' });
}

// Panel Participants
export async function getPanelParticipants(panelId: string) {
  return request<{ items: any[] }>(`/lab/panels/${panelId}/participants`);
}

export async function addPanelParticipant(panelId: string, data: { userId?: string; externalName?: string; externalEmail?: string; expertiseLevel?: string }) {
  return request<any>(`/lab/panels/${panelId}/participants`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function submitPanelResponse(panelId: string, data: { participantId: string; sampleCode: string; responses: any; overallScore?: number; comments?: string }) {
  return request<any>(`/lab/panels/${panelId}/responses`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Scale-Ups
export async function getScaleups(params?: { status?: string; formulaId?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.append('status', params.status);
  if (params?.formulaId) query.append('formulaId', params.formulaId);
  if (params?.search) query.append('search', params.search);
  if (params?.limit) query.append('limit', params.limit.toString());
  return request<{ items: any[]; total: number }>(`/lab/scaleups?${query.toString()}`);
}

export async function getScaleup(id: string) {
  return request<any>(`/lab/scaleups/${id}`);
}

export async function createScaleup(data: { title: string; formulaId?: string; trialId?: string; sourceBatchSize?: string; sourceUnit?: string; targetBatchSize?: string; targetUnit?: string; scaleFactor?: number; plannedDate?: string }) {
  return request<any>('/lab/scaleups', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateScaleup(id: string, data: Partial<{ title: string; status: string; actualDate: string; yieldAchieved: number; successStatus: string; results: any }>) {
  return request<any>(`/lab/scaleups/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function approveScaleup(id: string) {
  return request<any>(`/lab/scaleups/${id}/approve`, { method: 'POST' });
}

export async function deleteScaleup(id: string) {
  return request<{ success: boolean }>(`/lab/scaleups/${id}`, { method: 'DELETE' });
}

// Consumer Tests
export async function getConsumerTests(params?: { status?: string; type?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.append('status', params.status);
  if (params?.type) query.append('type', params.type);
  if (params?.search) query.append('search', params.search);
  if (params?.limit) query.append('limit', params.limit.toString());
  return request<{ items: any[]; total: number }>(`/lab/consumer-tests?${query.toString()}`);
}

export async function getConsumerTest(id: string) {
  return request<any>(`/lab/consumer-tests/${id}`);
}

export async function createConsumerTest(data: { title: string; description?: string; testType?: string; methodology?: string; sampleSize?: number; location?: string }) {
  return request<any>('/lab/consumer-tests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateConsumerTest(id: string, data: Partial<{ title: string; status: string; resultsSummary: any; insights: any }>) {
  return request<any>(`/lab/consumer-tests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteConsumerTest(id: string) {
  return request<{ success: boolean }>(`/lab/consumer-tests/${id}`, { method: 'DELETE' });
}

// ================================
// MRP (Material Requirements Planning)
// ================================

export async function getMrpRuns(params?: { status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ items: any[]; total: number }>(`/mrp/runs${queryString ? `?${queryString}` : ''}`);
}

export async function getMrpRun(id: string) {
  return request<any>(`/mrp/runs/${id}`);
}

export async function executeMrpRun(config: {
  runType?: string;
  planningHorizonStart?: string;
  planningHorizonEnd?: string;
  includedFormulas?: string[];
  includedWarehouses?: string[];
  includeSafetyStock?: boolean;
  includeLeadTimes?: boolean;
  includeMoq?: boolean;
  considerExistingOrders?: boolean;
  demandSource?: string;
}) {
  return request<any>('/mrp/runs', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

export async function getMrpPlannedOrders(params?: { status?: string; orderType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.orderType) query.set('orderType', params.orderType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ items: any[]; total: number }>(`/mrp/planned-orders${queryString ? `?${queryString}` : ''}`);
}

export async function firmMrpOrder(id: string) {
  return request<any>(`/mrp/planned-orders/${id}/firm`, { method: 'POST' });
}

export async function releaseMrpOrder(id: string) {
  return request<any>(`/mrp/planned-orders/${id}/release`, { method: 'POST' });
}

export async function getMrpWorkCenters(params?: { status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ items: any[]; total: number }>(`/mrp/work-centers${queryString ? `?${queryString}` : ''}`);
}

export async function getMrpProductionPlans(params?: { status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ items: any[]; total: number }>(`/mrp/plans${queryString ? `?${queryString}` : ''}`);
}

export async function createMrpProductionPlan(data: {
  planName: string;
  planType?: string;
  startDate: string;
  endDate: string;
  description?: string;
}) {
  return request<any>('/mrp/plans', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ================================
// Payments
// ================================

export async function getPayments(params?: {
  type?: string;
  status?: string;
  customerId?: string;
  supplierId?: string;
  paymentMethod?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}) {
  const query = new URLSearchParams();
  if (params?.type) query.set('type', params.type);
  if (params?.status) query.set('status', params.status);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.supplierId) query.set('supplierId', params.supplierId);
  if (params?.paymentMethod) query.set('paymentMethod', params.paymentMethod);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ payments: any[]; total: number }>(`/payments${queryString ? `?${queryString}` : ''}`);
}

export async function getPayment(id: string) {
  return request<any>(`/payments/${id}`);
}

export async function createPayment(data: {
  type: string;
  invoiceId?: string;
  customerId?: string;
  supplierId?: string;
  paymentDate: string;
  amount: string;
  currency?: string;
  exchangeRate?: string;
  paymentMethod: string;
  referenceNumber?: string;
  bankAccount?: string;
  notes?: string;
  status?: string;
}) {
  return request<any>('/payments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePayment(id: string, data: Partial<{
  paymentDate: string;
  amount: string;
  currency: string;
  exchangeRate: string;
  paymentMethod: string;
  referenceNumber: string;
  bankAccount: string;
  notes: string;
  status: string;
}>) {
  return request<any>(`/payments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function processPayment(id: string) {
  return request<any>(`/payments/${id}/process`, { method: 'POST' });
}

export async function failPayment(id: string, reason?: string) {
  return request<any>(`/payments/${id}/fail`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function refundPayment(id: string, data?: { amount?: string; reason?: string }) {
  return request<any>(`/payments/${id}/refund`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

export async function getPaymentSummary(params?: { startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  const queryString = query.toString();
  return request<{
    totalReceived: number;
    totalMade: number;
    netCashFlow: number;
    byMethod: any[];
  }>(`/payments/summary/period${queryString ? `?${queryString}` : ''}`);
}

export async function getPaymentMethodsBreakdown(year?: number) {
  const query = year ? `?year=${year}` : '';
  return request<{ year: number; methods: any[] }>(`/payments/summary/methods${query}`);
}

// ================================
// Sensory Evaluation
// ================================

export async function getSensoryDashboard() {
  return request<any>('/sensory/dashboard');
}

export async function getOlfactoryFamilies(params?: { parentId?: string; level?: number; active?: boolean }) {
  const query = new URLSearchParams();
  if (params?.parentId) query.append('parentId', params.parentId);
  if (params?.level) query.append('level', params.level.toString());
  if (params?.active !== undefined) query.append('active', params.active.toString());
  return request<{ items: any[] }>(`/sensory/families?${query.toString()}`);
}

export async function getOdorDescriptors(params?: { familyId?: string; type?: string; search?: string; active?: boolean; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.familyId) query.append('familyId', params.familyId);
  if (params?.type) query.append('type', params.type);
  if (params?.search) query.append('search', params.search);
  if (params?.active !== undefined) query.append('active', params.active.toString());
  if (params?.limit) query.append('limit', params.limit.toString());
  return request<{ items: any[] }>(`/sensory/descriptors?${query.toString()}`);
}

export async function getAromaProfiles(params?: { type?: string; familyId?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.type) query.append('type', params.type);
  if (params?.familyId) query.append('familyId', params.familyId);
  if (params?.search) query.append('search', params.search);
  if (params?.limit) query.append('limit', params.limit.toString());
  return request<{ items: any[]; total: number }>(`/sensory/aroma-profiles?${query.toString()}`);
}

export async function getFlavorProfiles(params?: { type?: string; flavorType?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.type) query.append('type', params.type);
  if (params?.flavorType) query.append('flavorType', params.flavorType);
  if (params?.search) query.append('search', params.search);
  if (params?.limit) query.append('limit', params.limit.toString());
  return request<{ items: any[]; total: number }>(`/sensory/flavor-profiles?${query.toString()}`);
}

export async function getSensorySessions(params?: { type?: string; status?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.type) query.append('type', params.type);
  if (params?.status) query.append('status', params.status);
  if (params?.search) query.append('search', params.search);
  if (params?.limit) query.append('limit', params.limit.toString());
  return request<{ items: any[]; total: number }>(`/sensory/sessions?${query.toString()}`);
}

export async function getSensoryPanelists(params?: { type?: string; status?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.type) query.append('type', params.type);
  if (params?.status) query.append('status', params.status);
  if (params?.search) query.append('search', params.search);
  if (params?.limit) query.append('limit', params.limit.toString());
  return request<{ items: any[]; total: number }>(`/sensory/panelists?${query.toString()}`);
}

export async function getSensoryTrends(params?: { type?: string; stage?: string; category?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.type) query.append('type', params.type);
  if (params?.stage) query.append('stage', params.stage);
  if (params?.category) query.append('category', params.category);
  if (params?.search) query.append('search', params.search);
  if (params?.limit) query.append('limit', params.limit.toString());
  return request<{ items: any[]; total: number }>(`/sensory/trends?${query.toString()}`);
}

// ================================
// Allergen Management
// ================================

export async function getAllergenDashboard() {
  return request<{
    allergenCategoriesCount: number;
    materialsWithAllergens: number;
    unverifiedAllergens: number;
    activeDeclarations: number;
    recentViolations: any[];
    recentActivity: any[];
  }>('/allergen-management/dashboard');
}

export async function getAllergenCategories(params?: {
  region?: string;
  isActive?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const query = new URLSearchParams();
  if (params?.region) query.set('region', params.region);
  if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/allergen-management/allergen-categories${queryString ? `?${queryString}` : ''}`);
}

export async function getAllergenCategory(id: string) {
  return request<any>(`/allergen-management/allergen-categories/${id}`);
}

export async function createAllergenCategory(data: {
  name: string;
  code: string;
  region?: string;
  description?: string;
  declarationText?: string;
  isActive?: boolean;
  sortOrder?: number;
}) {
  return request<any>('/allergen-management/allergen-categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAllergenCategory(id: string, data: Partial<{
  name: string;
  code: string;
  region: string;
  description: string;
  declarationText: string;
  isActive: boolean;
  sortOrder: number;
}>) {
  return request<any>(`/allergen-management/allergen-categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getDietaryCategories(params?: {
  categoryType?: string;
  isActive?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const query = new URLSearchParams();
  if (params?.categoryType) query.set('categoryType', params.categoryType);
  if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/allergen-management/dietary-categories${queryString ? `?${queryString}` : ''}`);
}

export async function createDietaryCategory(data: {
  name: string;
  code: string;
  categoryType?: string;
  description?: string;
  requirements?: string;
  isActive?: boolean;
  sortOrder?: number;
}) {
  return request<any>('/allergen-management/dietary-categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDietaryCategory(id: string, data: Partial<{
  name: string;
  code: string;
  categoryType: string;
  description: string;
  requirements: string;
  isActive: boolean;
  sortOrder: number;
}>) {
  return request<any>(`/allergen-management/dietary-categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getRegulatoryRestrictions(params?: {
  regulatoryBody?: string;
  restrictionType?: string;
  region?: string;
  isActive?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const query = new URLSearchParams();
  if (params?.regulatoryBody) query.set('regulatoryBody', params.regulatoryBody);
  if (params?.restrictionType) query.set('restrictionType', params.restrictionType);
  if (params?.region) query.set('region', params.region);
  if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/allergen-management/regulatory-restrictions${queryString ? `?${queryString}` : ''}`);
}

export async function getAllergenDeclarations(params?: {
  formulaId?: string;
  customerId?: string;
  status?: string;
  declarationType?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const query = new URLSearchParams();
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.status) query.set('status', params.status);
  if (params?.declarationType) query.set('declarationType', params.declarationType);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/allergen-management/declarations${queryString ? `?${queryString}` : ''}`);
}

export async function createAllergenDeclaration(data: {
  formulaId: string;
  customerId?: string;
  declarationType: string;
  declarationDate: string;
  effectiveDate?: string;
  expiryDate?: string;
  status?: string;
  containsAllergens?: any[];
  freeFromAllergens?: any[];
  mayContainAllergens?: any[];
  declarationText?: string;
  notes?: string;
}) {
  return request<any>('/allergen-management/declarations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ================================
// Maintenance Management
// ================================

export async function getWorkOrders(params?: {
  equipmentId?: string;
  status?: string;
  priority?: string;
  workOrderType?: string;
  assignedTo?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const query = new URLSearchParams();
  if (params?.equipmentId) query.set('equipmentId', params.equipmentId);
  if (params?.status) query.set('status', params.status);
  if (params?.priority) query.set('priority', params.priority);
  if (params?.workOrderType) query.set('workOrderType', params.workOrderType);
  if (params?.assignedTo) query.set('assignedTo', params.assignedTo);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/maintenance/work-orders${queryString ? `?${queryString}` : ''}`);
}

export async function getWorkOrder(id: string) {
  return request<any>(`/maintenance/work-orders/${id}`);
}

export async function createWorkOrder(data: {
  title: string;
  description?: string;
  workOrderType?: string;
  priority?: string;
  equipmentId?: string;
  categoryId?: string;
  assignedTo?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  estimatedDuration?: string;
}) {
  return request<any>('/maintenance/work-orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWorkOrder(id: string, data: Partial<{
  title: string;
  description: string;
  workOrderType: string;
  priority: string;
  status: string;
  assignedTo: string;
  scheduledStart: string;
  scheduledEnd: string;
  estimatedDuration: number;
}>) {
  return request<any>(`/maintenance/work-orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function startWorkOrder(id: string) {
  return request<any>(`/maintenance/work-orders/${id}/start`, { method: 'POST' });
}

export async function completeWorkOrder(id: string, completionData?: { notes?: string; actualDuration?: number }) {
  return request<any>(`/maintenance/work-orders/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(completionData || {}),
  });
}

export async function getPmSchedules(params?: { equipmentId?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.equipmentId) query.set('equipmentId', params.equipmentId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/maintenance/pm-schedules${queryString ? `?${queryString}` : ''}`);
}

export async function getOverduePmSchedules() {
  return request<any[]>('/maintenance/pm-schedules-overdue');
}

export async function getSpareParts(params?: { search?: string; category?: string; lowStock?: boolean; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.category) query.set('category', params.category);
  if (params?.lowStock !== undefined) query.set('lowStock', String(params.lowStock));
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/maintenance/spare-parts${queryString ? `?${queryString}` : ''}`);
}

export async function getSparePart(id: string) {
  return request<any>(`/maintenance/spare-parts/${id}`);
}

// ================================
// Training Management
// ================================

export async function getTrainingCategories() {
  return request<any[]>('/training/categories');
}

export async function createTrainingCategory(data: { name: string; code?: string; description?: string; displayOrder?: number }) {
  return request<any>('/training/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getTrainingCourses(params?: {
  status?: string;
  type?: string;
  categoryId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.type) query.set('type', params.type);
  if (params?.categoryId) query.set('categoryId', params.categoryId);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/training/courses${queryString ? `?${queryString}` : ''}`);
}

export async function getTrainingCourse(id: string) {
  return request<any>(`/training/courses/${id}`);
}

export async function createTrainingCourse(data: {
  name: string;
  courseCode?: string;
  courseType?: string;
  categoryId?: string;
  description?: string;
  durationHours?: number;
  maxParticipants?: number;
  isMandatory?: boolean;
  validityMonths?: number;
  status?: string;
}) {
  return request<any>('/training/courses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTrainingCourse(id: string, data: Partial<{
  name: string;
  courseCode: string;
  courseType: string;
  categoryId: string;
  description: string;
  durationHours: number;
  maxParticipants: number;
  isMandatory: boolean;
  validityMonths: number;
  status: string;
}>) {
  return request<any>(`/training/courses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteTrainingCourse(id: string) {
  return request<{ success: boolean }>(`/training/courses/${id}`, { method: 'DELETE' });
}

export async function getTrainingSessions(params?: {
  status?: string;
  courseId?: string;
  instructorId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.courseId) query.set('courseId', params.courseId);
  if (params?.instructorId) query.set('instructorId', params.instructorId);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/training/sessions${queryString ? `?${queryString}` : ''}`);
}

export async function getTrainingSession(id: string) {
  return request<any>(`/training/sessions/${id}`);
}

export async function createTrainingSession(data: {
  courseId: string;
  sessionCode?: string;
  instructorId?: string;
  scheduledDate: string;
  startTime?: string;
  endTime?: string;
  location?: string;
  maxParticipants?: number;
  status?: string;
}) {
  return request<any>('/training/sessions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTrainingSession(id: string, data: Partial<{
  instructorId: string;
  scheduledDate: string;
  startTime: string;
  endTime: string;
  location: string;
  maxParticipants: number;
  status: string;
  completedAt: string;
}>) {
  return request<any>(`/training/sessions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getTrainingDashboard() {
  return request<{
    courses: { total: number; active: number; mandatory: number };
    sessions: { total: number; scheduled: number; upcoming: number; completed: number };
    enrollments: { total: number; pending: number; completed: number; avgScore: number };
    expiringCertifications: any[];
    upcomingSessions: any[];
  }>('/training/dashboard');
}

export async function getTrainingEnrollments(params?: { sessionId?: string; employeeId?: string; status?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.sessionId) query.set('sessionId', params.sessionId);
  if (params?.employeeId) query.set('employeeId', params.employeeId);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<any[]>(`/training/enrollments${queryString ? `?${queryString}` : ''}`);
}

export async function createTrainingEnrollment(data: { sessionId: string; employeeId: string }) {
  return request<any>('/training/enrollments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getCompetencies(params?: { type?: string; category?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.type) query.set('type', params.type);
  if (params?.category) query.set('category', params.category);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<any[]>(`/training/competencies${queryString ? `?${queryString}` : ''}`);
}

export async function getTrainingInstructors(params?: { type?: string; status?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.type) query.set('type', params.type);
  if (params?.status) query.set('status', params.status);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<any[]>(`/training/instructors${queryString ? `?${queryString}` : ''}`);
}

// ================================
// Projects
// ================================

export async function getProjectsDashboard() {
  return request<{
    projects: { total: number; planning: number; active: number; onHold: number; completed: number };
    tasks: { total: number; todo: number; inProgress: number; completed: number; overdue: number };
    myTasks: any[];
    recentProjects: any[];
  }>('/projects/dashboard/overview');
}

export async function getProjects(params?: { projectType?: string; status?: string; customerId?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.projectType) query.set('projectType', params.projectType);
  if (params?.status) query.set('status', params.status);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/projects${queryString ? `?${queryString}` : ''}`);
}

export async function getProject(id: string) {
  return request<any>(`/projects/${id}`);
}

export async function createProject(data: Record<string, any>) {
  return request<any>('/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProject(id: string, data: Record<string, any>) {
  return request<any>(`/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateProjectStatus(id: string, status: string) {
  return request<any>(`/projects/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

export async function getProjectMilestones(projectId: string) {
  return request<{ data: any[] }>(`/projects/${projectId}/milestones`);
}

export async function createProjectMilestone(projectId: string, data: Record<string, any>) {
  return request<any>(`/projects/${projectId}/milestones`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getProjectTasks(projectId: string, params?: { status?: string; milestoneId?: string; assignedTo?: string }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.milestoneId) query.set('milestoneId', params.milestoneId);
  if (params?.assignedTo) query.set('assignedTo', params.assignedTo);
  const queryString = query.toString();
  return request<{ data: any[] }>(`/projects/${projectId}/tasks${queryString ? `?${queryString}` : ''}`);
}

export async function createProjectTask(projectId: string, data: Record<string, any>) {
  return request<any>(`/projects/${projectId}/tasks`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProjectTask(taskId: string, data: Record<string, any>) {
  return request<any>(`/projects/tasks/${taskId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ================================
// Calibration
// ================================

export async function getCalibrationDashboard() {
  return request<{
    requirementsByStatus: any[];
    overdueCount: number;
    upcomingCalibrations: any[];
    openOotCount: number;
    expiringStandards: any[];
    recentCalibrations: any[];
    calibrationsThisMonth: number;
  }>('/calibration/dashboard');
}

export async function getCalibrationRequirements(params?: { equipmentId?: string; calibrationStatus?: string; gmpCritical?: boolean; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.equipmentId) query.set('equipmentId', params.equipmentId);
  if (params?.calibrationStatus) query.set('calibrationStatus', params.calibrationStatus);
  if (params?.gmpCritical !== undefined) query.set('gmpCritical', params.gmpCritical.toString());
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/calibration/requirements${queryString ? `?${queryString}` : ''}`);
}

export async function getCalibrationRecords(params?: { equipmentId?: string; passFail?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.equipmentId) query.set('equipmentId', params.equipmentId);
  if (params?.passFail) query.set('passFail', params.passFail);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/calibration/records${queryString ? `?${queryString}` : ''}`);
}

export async function getCalibrationStandards(params?: { status?: string; standardType?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.standardType) query.set('standardType', params.standardType);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/calibration/standards${queryString ? `?${queryString}` : ''}`);
}

export async function getCalibrationSchedule(params?: { equipmentId?: string; status?: string; startDate?: string; endDate?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.equipmentId) query.set('equipmentId', params.equipmentId);
  if (params?.status) query.set('status', params.status);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/calibration/schedule${queryString ? `?${queryString}` : ''}`);
}

export async function getOotReports(params?: { equipmentId?: string; severity?: string; status?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.equipmentId) query.set('equipmentId', params.equipmentId);
  if (params?.severity) query.set('severity', params.severity);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/calibration/oot-reports${queryString ? `?${queryString}` : ''}`);
}

// ================================
// Production Scheduling
// ================================

export async function getProductionLines(params?: { search?: string; status?: string; lineType?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.status) query.set('status', params.status);
  if (params?.lineType) query.set('lineType', params.lineType);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/scheduling/lines${queryString ? `?${queryString}` : ''}`);
}

export async function getProductionLine(id: string) {
  return request<any>(`/scheduling/lines/${id}`);
}

export async function getWorkShifts(params?: { isActive?: boolean }) {
  const query = new URLSearchParams();
  if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
  const queryString = query.toString();
  return request<any[]>(`/scheduling/shifts${queryString ? `?${queryString}` : ''}`);
}

export async function getProductionSchedules(params?: { status?: string; productionLineId?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.productionLineId) query.set('productionLineId', params.productionLineId);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/scheduling/schedules${queryString ? `?${queryString}` : ''}`);
}

export async function getCapacityBuckets(params?: { productionLineId?: string; startDate?: string; endDate?: string; status?: string }) {
  const query = new URLSearchParams();
  if (params?.productionLineId) query.set('productionLineId', params.productionLineId);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.status) query.set('status', params.status);
  const queryString = query.toString();
  return request<any[]>(`/scheduling/capacity${queryString ? `?${queryString}` : ''}`);
}

export async function getProductionCalendar(params?: { productionLineId?: string; startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params?.productionLineId) query.set('productionLineId', params.productionLineId);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  const queryString = query.toString();
  return request<any[]>(`/scheduling/calendar${queryString ? `?${queryString}` : ''}`);
}

// ================================
// Environment, Health & Safety (EHS)
// ================================

export async function getSafetyIncidents(params?: { search?: string; status?: string; severity?: string; type?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.status) query.set('status', params.status);
  if (params?.severity) query.set('severity', params.severity);
  if (params?.type) query.set('type', params.type);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/ehs/incidents${queryString ? `?${queryString}` : ''}`);
}

export async function getSafetyIncident(id: string) {
  return request<any>(`/ehs/incidents/${id}`);
}

export async function createSafetyIncident(data: {
  type: string;
  title: string;
  description: string;
  severity: string;
  location: string;
  occurredAt: string;
  reportedBy?: string;
}) {
  return request<any>('/ehs/incidents', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSafetyIncident(id: string, data: {
  status?: string;
  investigation?: string;
  correctiveActions?: string;
  closedAt?: string;
}) {
  return request<any>(`/ehs/incidents/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function getSafetyInspections(params?: { status?: string; type?: string; areaId?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.type) query.set('type', params.type);
  if (params?.areaId) query.set('areaId', params.areaId);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/ehs/inspections${queryString ? `?${queryString}` : ''}`);
}

export async function createSafetyInspection(data: {
  type: string;
  areaId: string;
  scheduledDate: string;
  inspectorId?: string;
}) {
  return request<any>('/ehs/inspections', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function completeSafetyInspection(id: string, data: {
  findings: string;
  score?: number;
  issues?: { description: string; severity: string; corrective_action?: string }[];
}) {
  return request<any>(`/ehs/inspections/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getPpeInventory(params?: { category?: string; status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.category) query.set('category', params.category);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/ehs/ppe${queryString ? `?${queryString}` : ''}`);
}

export async function createPpeItem(data: {
  name: string;
  category: string;
  stockQuantity: number;
  minimumStock: number;
  unit: string;
  supplier?: string;
}) {
  return request<any>('/ehs/ppe', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePpeStock(id: string, data: { quantity: number; reason: string }) {
  return request<any>(`/ehs/ppe/${id}/stock`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getEmergencyPlans(params?: { type?: string; status?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.type) query.set('type', params.type);
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/ehs/emergency-plans${queryString ? `?${queryString}` : ''}`);
}

export async function getEmergencyDrills(params?: { planId?: string; status?: string; startDate?: string; endDate?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.planId) query.set('planId', params.planId);
  if (params?.status) query.set('status', params.status);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/ehs/drills${queryString ? `?${queryString}` : ''}`);
}

export async function scheduleEmergencyDrill(data: {
  emergencyPlanId: string;
  scheduledDate: string;
  description?: string;
}) {
  return request<any>('/ehs/drills', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function completeEmergencyDrill(id: string, data: {
  participants: number;
  evacuationTime?: number;
  findings?: string;
  score?: number;
}) {
  return request<any>(`/ehs/drills/${id}/complete`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getEhsStats() {
  return request<{
    openIncidents: number;
    incidentsByStatus: { status: string; count: number }[];
    upcomingInspections: number;
    overdueInspections: number;
    lowStockPpe: number;
    scheduledDrills: number;
    safetyScore: number;
  }>('/ehs/stats');
}

// ================================
// Analytics & KPIs
// ================================

export async function getKpis(params?: { category?: string; isActive?: boolean }) {
  const query = new URLSearchParams();
  if (params?.category) query.set('category', params.category);
  if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
  const queryString = query.toString();
  return request<{ data: any[] }>(`/analytics/kpis${queryString ? `?${queryString}` : ''}`);
}

export async function getKpi(id: string) {
  return request<any>(`/analytics/kpis/${id}`);
}

export async function createKpi(data: Record<string, any>) {
  return request<any>('/analytics/kpis', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getKpiSnapshots(kpiId: string, params?: { startDate?: string; endDate?: string }) {
  const query = new URLSearchParams();
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  const queryString = query.toString();
  return request<any[]>(`/analytics/kpis/${kpiId}/snapshots${queryString ? `?${queryString}` : ''}`);
}

export async function getDashboards() {
  return request<{ data: any[] }>('/analytics/dashboards');
}

export async function getDashboard(id: string) {
  return request<any>(`/analytics/dashboards/${id}`);
}

export async function createDashboard(data: Record<string, any>) {
  return request<any>('/analytics/dashboards', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getAnalyticsSummary() {
  return request<{
    data: {
      revenue: { monthToDate: number; yearToDate: number; invoiceCount: number };
      production: { totalBatches: number; completedBatches: number; totalProduced: number };
      quality: { totalTests: number; passRate: number; failedTests: number };
      inventory: { totalValue: number; lowStockAlerts: number; totalItems: number };
      customers: { total: number; active: number; newThisMonth: number };
      orders: { pending: number; confirmed: number; inProduction: number; completed: number; pipelineValue: number };
    };
  }>('/analytics/executive-summary');
}

// ================================
// Audit Trail
// ================================

export async function getAuditTrail(params?: { entityType?: string; entityId?: string; action?: string; userId?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.entityType) query.set('entityType', params.entityType);
  if (params?.entityId) query.set('entityId', params.entityId);
  if (params?.action) query.set('action', params.action);
  if (params?.userId) query.set('userId', params.userId);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/audit/trail${queryString ? `?${queryString}` : ''}`);
}

export async function getAuditEntry(id: string) {
  return request<any>(`/audit/trail/${id}`);
}

export async function getEntityHistory(entityType: string, entityId: string) {
  return request<{ data: any[] }>(`/audit/trail/entity/${entityType}/${entityId}`);
}

export async function getAccessLogs(params?: { userId?: string; action?: string; startDate?: string; endDate?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.userId) query.set('userId', params.userId);
  if (params?.action) query.set('action', params.action);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/audit/access-logs${queryString ? `?${queryString}` : ''}`);
}

// ================================
// Packaging
// ================================

export async function getPackagingTypes(params?: { category?: string; material?: string; isActive?: boolean; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.category) query.set('category', params.category);
  if (params?.material) query.set('material', params.material);
  if (params?.isActive !== undefined) query.set('isActive', String(params.isActive));
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/packaging/types${queryString ? `?${queryString}` : ''}`);
}

export async function getPackagingType(id: string) {
  return request<any>(`/packaging/types/${id}`);
}

export async function createPackagingType(data: Record<string, any>) {
  return request<any>('/packaging/types', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getLabelTemplates(params?: { status?: string; formulaId?: string; search?: string; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.formulaId) query.set('formulaId', params.formulaId);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/packaging/labels${queryString ? `?${queryString}` : ''}`);
}

export async function getPackagingInventory(params?: { packagingTypeId?: string; lowStock?: boolean; limit?: number }) {
  const query = new URLSearchParams();
  if (params?.packagingTypeId) query.set('packagingTypeId', params.packagingTypeId);
  if (params?.lowStock !== undefined) query.set('lowStock', String(params.lowStock));
  if (params?.limit) query.set('limit', params.limit.toString());
  const queryString = query.toString();
  return request<{ data: any[]; total: number }>(`/packaging/inventory${queryString ? `?${queryString}` : ''}`);
}

export async function getPackagingDashboard() {
  return request<{
    totalTypes: number;
    lowStockItems: number;
    recentUsage: any[];
    topPackaging: any[];
  }>('/packaging/dashboard');
}

// ================================
// Customer Feedback
// ================================

export async function getCustomerFeedback(params?: { search?: string; status?: string; feedbackType?: string; customerId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.status) query.set('status', params.status);
  if (params?.feedbackType) query.set('feedbackType', params.feedbackType);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/customer-feedback/feedback${queryString ? `?${queryString}` : ''}`);
}

export async function getCustomerFeedbackItem(id: string) {
  return request<any>(`/customer-feedback/feedback/${id}`);
}

export async function createCustomerFeedback(data: {
  customerId?: string;
  customerName: string;
  feedbackType: string;
  subject: string;
  description: string;
  productId?: string;
  orderId?: string;
}) {
  return request<any>('/customer-feedback/feedback', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCustomerFeedback(id: string, data: { status?: string; response?: string; assignedTo?: string }) {
  return request<any>(`/customer-feedback/feedback/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getQualityComplaints(params?: { status?: string; severity?: string; customerId?: string; search?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.severity) query.set('severity', params.severity);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.search) query.set('search', params.search);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/customer-feedback/complaints${queryString ? `?${queryString}` : ''}`);
}

export async function createQualityComplaint(data: {
  customerId: string;
  customerName: string;
  subject: string;
  description: string;
  severity: string;
  productId?: string;
  batchId?: string;
  orderId?: string;
}) {
  return request<any>('/customer-feedback/complaints', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getCustomerReturns(params?: { status?: string; customerId?: string; returnReason?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.customerId) query.set('customerId', params.customerId);
  if (params?.returnReason) query.set('returnReason', params.returnReason);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/customer-feedback/returns${queryString ? `?${queryString}` : ''}`);
}

export async function createCustomerReturn(data: {
  customerId: string;
  orderId?: string;
  returnReason: string;
  description: string;
  items: { productId: string; quantity: number; reason: string }[];
}) {
  return request<any>('/customer-feedback/returns', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getSatisfactionSurveys(params?: { status?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<any[]>(`/customer-feedback/surveys${queryString ? `?${queryString}` : ''}`);
}

export async function createSatisfactionSurvey(data: {
  title: string;
  description?: string;
  questions: { question: string; type: string; options?: string[] }[];
}) {
  return request<any>('/customer-feedback/surveys', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ================================
// Electronic Batch Records (EBR)
// ================================

export async function getMasterBatchRecords(params?: { search?: string; status?: string; productId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.status) query.set('status', params.status);
  if (params?.productId) query.set('productId', params.productId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ items: any[]; total: number }>(`/ebr/mbr${queryString ? `?${queryString}` : ''}`);
}

export async function getMasterBatchRecord(id: string) {
  return request<any>(`/ebr/mbr/${id}`);
}

export async function createMasterBatchRecord(data: {
  title: string;
  productName: string;
  productCode?: string;
  batchSizeMin: number;
  batchSizeMax: number;
  batchUnit: string;
  sections?: { title: string; sequenceOrder: number; steps?: { title: string; instructions: string; sequenceOrder: number }[] }[];
}) {
  return request<any>('/ebr/mbr', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getElectronicBatchRecords(params?: { search?: string; status?: string; mbrId?: string; productionOrderId?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.status) query.set('status', params.status);
  if (params?.mbrId) query.set('mbrId', params.mbrId);
  if (params?.productionOrderId) query.set('productionOrderId', params.productionOrderId);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ items: any[]; total: number }>(`/ebr/records${queryString ? `?${queryString}` : ''}`);
}

export async function getElectronicBatchRecord(id: string) {
  return request<any>(`/ebr/records/${id}`);
}

export async function createElectronicBatchRecord(data: {
  mbrId: string;
  productionOrderId?: string;
  batchNumber: string;
  batchSize: number;
  batchUnit: string;
}) {
  return request<any>('/ebr/records', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateEbrStep(ebrId: string, stepId: string, data: {
  actualValue?: string;
  notes?: string;
  completedAt?: string;
  completedBy?: string;
}) {
  return request<any>(`/ebr/records/${ebrId}/steps/${stepId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function getEbrDeviations(params?: { ebrId?: string; status?: string; severity?: string; limit?: number; offset?: number }) {
  const query = new URLSearchParams();
  if (params?.ebrId) query.set('ebrId', params.ebrId);
  if (params?.status) query.set('status', params.status);
  if (params?.severity) query.set('severity', params.severity);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  const queryString = query.toString();
  return request<{ items: any[]; total: number }>(`/ebr/deviations${queryString ? `?${queryString}` : ''}`);
}

export async function createEbrDeviation(data: {
  ebrId: string;
  sectionId?: string;
  stepId?: string;
  deviationType: string;
  description: string;
  severity: string;
  impact?: string;
}) {
  return request<any>('/ebr/deviations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function signEbr(ebrId: string, data: {
  signatureType: string;
  pin?: string;
  reason?: string;
}) {
  return request<any>(`/ebr/records/${ebrId}/sign`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}
